/**
 * Pre-signed S3 uploads (Phase 8 §3.4, Q2).
 *
 * Flow: ask the entity's `…/upload-url` route for a ticket → POST the file straight to S3 with the
 * signed fields (apiClient.uploadToPresignedPost, real progress) → the caller confirms by POSTing
 * `{ key, documentName, documentType }` to the entity's documents route, where the server checks
 * size and magic bytes and deletes the object on failure.
 *
 * Used by PA documents, patient documents library, consent and POC signed copies.
 */

import { apiClient } from '../api-client';

/** Ticket returned by any `…/documents/upload-url` route (pre-signed S3 POST). */
export interface UploadTicket {
  uploadUrl: string;
  fields: Record<string, string>;
  key: string;
  /** Seconds until the ticket expires (300 on the backend). */
  expiresIn: number;
}

/** Body every `…/upload-url` route accepts. */
export interface UploadTicketRequest {
  fileName: string;
  contentType: string;
  size: number;
}

export interface PresignedUploadResult {
  key: string;
}

/** Thrown before any network call when the file fails the client-side checks. */
export class UploadValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadValidationError';
  }
}

export const UPLOAD_ALLOWED_TYPES: readonly string[] = ['application/pdf', 'image/jpeg', 'image/png'];
export const UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

const EXTENSION_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

/** Content type to declare for the file: its MIME type, or one inferred from the extension. */
export function resolveContentType(file: File): string {
  const type = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
  if (type) return type;
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  return EXTENSION_TYPES[ext] || '';
}

/** Returns a friendly message when the file can't be uploaded, otherwise null. */
export function validateUploadFile(file: File): string | null {
  if (!UPLOAD_ALLOWED_TYPES.includes(resolveContentType(file))) {
    return `"${file.name}" can't be uploaded. Only PDF, JPG and PNG files are allowed.`;
  }
  if (file.size <= 0) {
    return `"${file.name}" is empty.`;
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return `"${file.name}" is ${mb} MB. Files must be 10 MB or smaller.`;
  }
  return null;
}

/** Ticket request body for a file (what `getTicket` callers usually send). */
export function ticketRequestFor(file: File): UploadTicketRequest {
  return { fileName: file.name, contentType: resolveContentType(file), size: file.size };
}

export class PresignedUploadService {
  /**
   * Validate the file, get a ticket and POST the file to S3.
   * Returns the S3 key for the caller's confirm call. Never sends the file through API Gateway.
   */
  static async upload(
    file: File,
    getTicket: () => Promise<UploadTicket>,
    onProgress?: (pct: number) => void
  ): Promise<PresignedUploadResult> {
    const invalid = validateUploadFile(file);
    if (invalid) throw new UploadValidationError(invalid);

    const ticket = await getTicket();
    if (!ticket || !ticket.uploadUrl || !ticket.key || !ticket.fields) {
      throw new Error('The server did not return an upload link. Try again.');
    }

    onProgress?.(0);
    await apiClient.uploadToPresignedPost(ticket.uploadUrl, ticket.fields, file, onProgress);
    return { key: ticket.key };
  }

  /**
   * upload() followed by the caller's confirm call. A confirm failure (e.g. 422 when the server's
   * magic-byte check fails and the object is deleted) is re-thrown so the UI can show it.
   */
  static async uploadAndConfirm<T>(
    file: File,
    getTicket: () => Promise<UploadTicket>,
    confirm: (key: string) => Promise<T>,
    onProgress?: (pct: number) => void
  ): Promise<T> {
    const { key } = await PresignedUploadService.upload(file, getTicket, onProgress);
    return confirm(key);
  }
}

export default PresignedUploadService;
